import { FormulaErrorCodes, FormulaParseError, formulaError } from './errors.js';
export function tokenSpan(token) {
    return [token.start, token.end];
}
const TWO_CHAR_OPERATORS = ['??', '||', '&&', '==', '!=', '<=', '>='];
const ONE_CHAR_OPERATORS = '?:<>+-*/!(),.';
/**
 * §4.1 lexical rules: ASCII identifiers `[A-Za-z_][A-Za-z0-9_]*`, numbers `[0-9]+ ('.' [0-9]+)?` without exponent,
 * single-quoted strings with `''` as the quote, insignificant whitespace, no comments.
 */
/**
 * The whitespace of §4.1: tab, line feed, carriage return, space and the no-break space — exactly these, in both
 * implementations. A regex `\s` or C#'s `char.IsWhiteSpace` would each take a different further set (U+FEFF here,
 * U+0085 there), so the same text would parse in one and not the other.
 */
const WHITESPACE = new Set(['\t', '\n', '\r', ' ', '\u00A0']);
export function tokenize(text) {
    const lexer = new Lexer(text);
    const tokens = [];
    let token;
    do {
        token = lexer.next();
        tokens.push(token);
    } while (token.kind !== 'end');
    return tokens;
}
class Lexer {
    constructor(text) {
        this.text = text;
        this.pos = 0;
    }
    next() {
        while (this.pos < this.text.length && WHITESPACE.has(this.text[this.pos]))
            this.pos++;
        if (this.pos >= this.text.length)
            return { kind: 'end', text: '', start: this.pos, end: this.pos };
        const c = this.text[this.pos];
        if (isDigit(c))
            return this.readNumber();
        if (c === "'")
            return this.readString();
        if (isIdentifierStart(c))
            return this.readIdentifier();
        return this.readOperator();
    }
    readNumber() {
        const start = this.pos;
        while (this.pos < this.text.length && isDigit(this.text[this.pos]))
            this.pos++;
        if (this.pos < this.text.length && this.text[this.pos] === '.') {
            if (this.pos + 1 >= this.text.length || !isDigit(this.text[this.pos + 1])) {
                throw parseError([this.pos, this.pos + 1], 'A digit must follow the decimal point.');
            }
            this.pos++;
            while (this.pos < this.text.length && isDigit(this.text[this.pos]))
                this.pos++;
        }
        return { kind: 'number', text: this.text.slice(start, this.pos), start, end: this.pos };
    }
    readString() {
        const start = this.pos;
        let value = '';
        this.pos++;
        while (this.pos < this.text.length) {
            const c = this.text[this.pos];
            if (c === "'" && this.text[this.pos + 1] === "'") {
                value += "'";
                this.pos += 2;
                continue;
            }
            if (c === "'") {
                this.pos++;
                return { kind: 'string', text: value, start, end: this.pos };
            }
            value += c;
            this.pos++;
        }
        throw parseError([start, this.text.length], 'The string is not terminated; close it with a single quote.');
    }
    readIdentifier() {
        const start = this.pos;
        while (this.pos < this.text.length && isIdentifierPart(this.text[this.pos]))
            this.pos++;
        return { kind: 'identifier', text: this.text.slice(start, this.pos), start, end: this.pos };
    }
    readOperator() {
        for (const op of TWO_CHAR_OPERATORS) {
            if (this.text.startsWith(op, this.pos)) {
                this.pos += op.length;
                return { kind: 'punctuation', text: op, start: this.pos - op.length, end: this.pos };
            }
        }
        const c = this.text[this.pos];
        if (ONE_CHAR_OPERATORS.includes(c)) {
            this.pos++;
            return { kind: 'punctuation', text: c, start: this.pos - 1, end: this.pos };
        }
        throw parseError([this.pos, this.pos + 1], `Unexpected character '${c}'.`);
    }
}
function isDigit(c) {
    return c >= '0' && c <= '9';
}
function isIdentifierStart(c) {
    return (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_';
}
function isIdentifierPart(c) {
    return isIdentifierStart(c) || isDigit(c);
}
function parseError(span, message) {
    return new FormulaParseError(formulaError(FormulaErrorCodes.ParseError, span, message));
}
